import React, { useEffect, useState, useCallback } from 'react';
import { Container, Button, Form, Alert, Pagination, Placeholder, Row, Col, Modal } from 'react-bootstrap';
import { getStrategies, createStrategy, updateStrategy, deleteStrategy } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

// Custom styles for search highlighting
const customStyles = `
  .modal-600w {
    max-width: 650px !important;
  }

  .search-highlight {
    background-color: #fff3cd !important;
    color: #856404 !important;
    padding: 0.1em 0.2em !important;
    border-radius: 0.2em !important;
    font-weight: 500 !important;
  }

  [data-theme="dark"] .search-highlight {
    background-color: #664d03 !important;
    color: #ffda6a !important;
  }

  .strategy-card {
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    box-shadow: 0 0.35rem 1rem rgba(15, 23, 42, 0.05);
    transition: background-color 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
    position: relative;
    overflow: hidden;
  }

  .strategy-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 0.6rem 1.45rem rgba(15, 23, 42, 0.1);
  }

  .strategy-card-draggable {
    cursor: grab;
  }

  .strategy-card-draggable:active {
    cursor: grabbing;
  }

  .strategy-card-dragging {
    opacity: 0.85;
  }

  .strategy-card-pinned {
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.16), rgba(13, 110, 253, 0.05));
    border-color: rgba(13, 110, 253, 0.45) !important;
    box-shadow: 0 0.95rem 2.2rem rgba(13, 110, 253, 0.28);
  }

  .strategy-card-pinned::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 2px solid rgba(13, 110, 253, 0.35);
    pointer-events: none;
    opacity: 0.85;
  }

  [data-theme="dark"] .strategy-card {
    box-shadow: 0 0.45rem 1.1rem rgba(0, 0, 0, 0.4);
  }

  [data-theme="dark"] .strategy-card:hover {
    box-shadow: 0 0.7rem 1.7rem rgba(0, 0, 0, 0.55);
  }

  [data-theme="dark"] .strategy-card-pinned {
    background: linear-gradient(135deg, rgba(46, 59, 92, 0.82), rgba(110, 168, 254, 0.28));
    border-color: rgba(110, 168, 254, 0.55) !important;
    box-shadow: 0 1.05rem 2.45rem rgba(13, 110, 253, 0.35);
  }

  [data-theme="dark"] .strategy-card-pinned::after {
    border-color: rgba(110, 168, 254, 0.5);
    opacity: 0.9;
  }

  .strategy-card-drag-over {
    border-color: var(--bs-primary) !important;
    box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.25);
  }

  .strategy-card-drag-over::after {
    border-color: transparent;
  }

`;

const sortStrategyItems = (list) => {
  return [...list].sort((a, b) => {
    const aPinned = a.pinned ? 1 : 0;
    const bPinned = b.pinned ? 1 : 0;
    if (aPinned !== bPinned) {
      return bPinned - aPinned;
    }
    const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
    const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
    return dateB - dateA;
  });
};

const STRATEGY_ORDER_STORAGE_KEY = 'strategyOrder';

const getStoredOrder = () => {
  try {
    const raw = localStorage.getItem(STRATEGY_ORDER_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((value) => String(value));
  } catch (error) {
    return [];
  }
};

const persistOrder = (list) => {
  try {
    const ids = list.map((item) => item.id);
    localStorage.setItem(STRATEGY_ORDER_STORAGE_KEY, JSON.stringify(ids));
  } catch (error) {
    // Silently ignore storage errors (quota, disabled, etc.)
  }
};

const removeFromStoredOrder = (idToRemove) => {
  try {
    const storedOrder = getStoredOrder();
    if (!storedOrder.length) return;
    const filtered = storedOrder.filter((storedId) => storedId !== String(idToRemove));
    localStorage.setItem(STRATEGY_ORDER_STORAGE_KEY, JSON.stringify(filtered));
  } catch (error) {
    // Ignore storage errors
  }
};

const applyStoredOrder = (list) => {
  const storedOrder = getStoredOrder();
  if (!storedOrder.length) return list;

  const mappedItems = new Map(list.map((item) => [String(item.id), item]));
  const seen = new Set();
  const orderedPinned = [];
  const orderedUnpinned = [];

  for (const id of storedOrder) {
    const item = mappedItems.get(String(id));
    if (!item) continue;
    seen.add(String(id));
    if (item.pinned) {
      orderedPinned.push(item);
    } else {
      orderedUnpinned.push(item);
    }
  }

  const newPinned = [];
  const newUnpinned = [];

  for (const item of list) {
    const key = String(item.id);
    if (seen.has(key)) continue;
    if (item.pinned) {
      newPinned.push(item);
    } else {
      newUnpinned.push(item);
    }
  }

  return [...orderedPinned, ...newPinned, ...newUnpinned, ...orderedUnpinned];
};

// Repositions only the toggled item, preserving the rest of the list's
// current (possibly manually drag-and-dropped) order -- unlike
// sortStrategyItems, which fully re-sorts the whole list by
// pinned-status-then-date and would discard any custom order on every
// pin/unpin. Mirrors GLeaders.js/Themes.js/Wisdom.js's identical helper.
const reorderAfterPinChange = (list, updatedItem) => {
  const updatedList = list.map((item) =>
    item.id === updatedItem.id ? { ...item, ...updatedItem } : item
  );
  const target = updatedList.find((item) => item.id === updatedItem.id);
  if (!target) {
    return updatedList;
  }

  if (target.pinned) {
    // Keep existing pinned order and append the newly pinned strategy to the end of pinned items.
    const pinnedItems = [];
    const unpinnedItems = [];

    updatedList.forEach((item) => {
      if (item.id === target.id) return;
      if (item.pinned) {
        pinnedItems.push(item);
      } else {
        unpinnedItems.push(item);
      }
    });

    pinnedItems.push(target);
    return [...pinnedItems, ...unpinnedItems];
  }

  // When unpinning, preserve the current order of remaining pinned items and unpinned items.
  const pinnedItems = [];
  const unpinnedItems = [];

  updatedList.forEach((item) => {
    if (item.pinned) {
      pinnedItems.push(item);
    } else {
      unpinnedItems.push(item);
    }
  });

  return [...pinnedItems, ...unpinnedItems];
};

function Strategies() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingEntryConditions, setEditingEntryConditions] = useState('');
  const [editingTradingPlan, setEditingTradingPlan] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('strategyPageSize');
    const n = saved ? parseInt(saved) : 10;
    return [3,5,10].includes(n) ? n : 10;
  });
  const [numColumns, setNumColumns] = useState(() => {
    const saved = localStorage.getItem('strategyColumns');
    const n = saved ? parseInt(saved) : 3;
    return [1,2,3,4].includes(n) ? n : 3;
  });
  const [query, setQuery] = useState('');
  const [newName, setNewName] = useState('');
  const [newEntryConditions, setNewEntryConditions] = useState('');
  const [newTradingPlan, setNewTradingPlan] = useState('');
  const [, setTotalPages] = useState(1);
  const [, setTotalCount] = useState(0);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const normalizedQuery = query.trim();
      const resp = await getStrategies(1, 1000, normalizedQuery);
      const list = sortStrategyItems(resp.data.items || []);
      const orderedList = applyStoredOrder(list);
      setItems(orderedList);
      // Calculate pages based on rows/columns layout
      const count = orderedList.length;
      const notesPerRow = numColumns;
      const totalRows = Math.ceil(count / notesPerRow);
      const pages = Math.max(1, Math.ceil(totalRows / pageSize));
      setTotalPages(pages);
      setTotalCount(count);
      setCurrentPage(1);
      if (!normalizedQuery) {
        persistOrder(orderedList);
      }
    } catch (e) {
      setError('Failed to load strategies.');
    } finally {
      setLoading(false);
    }
  }, [query, numColumns, pageSize]);

  // Initial load handled by query effect (debounced) to avoid double fetch and visual shake
  useEffect(() => { // initial load & reload when query changes
    const t = setTimeout(() => { load(); }, 500); // Increased debounce time to 500ms
    return () => clearTimeout(t);
  }, [load]);

  // Delay showing skeletons to prevent brief flash and limit count
  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => setShowSkeleton(true), 200);
    } else {
      setShowSkeleton(false);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [loading]);

  const handleAdd = async () => {
    if (!newName.trim() || !newEntryConditions.trim() || !newTradingPlan.trim()) return;
    try {
      setSaving(true);
      await createStrategy(newName.trim(), newEntryConditions.trim(), newTradingPlan.trim());
      setNewName('');
      setNewEntryConditions('');
      setNewTradingPlan('');
      await load();
      setShowModal(false);
    } catch (e) {
      setError('Failed to add strategy');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenModal = () => {
    setNewName('');
    setNewEntryConditions('');
    setNewTradingPlan('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (saving) return;
    setShowModal(false);
    setNewName('');
    setNewEntryConditions('');
    setNewTradingPlan('');
  };

  const handleEdit = async (id) => {
    try {
      const resp = await updateStrategy(id, {
        name: editingName.trim(),
        entry_conditions: editingEntryConditions.trim(),
        trading_plan: editingTradingPlan.trim()
      });
      const updated = resp.data;
      setItems(prev => {
        const next = prev.map(x => x.id === id ? { ...x, ...updated } : x);
        if (!query.trim()) {
          persistOrder(next);
        }
        return next;
      });
      setEditingId(null);
      setEditingName('');
      setEditingEntryConditions('');
      setEditingTradingPlan('');
    } catch (e) {
      setError('Failed to save strategy');
    }
  };

  const handleTogglePinned = async (id, pinned) => {
    try {
      setError('');
      const resp = await updateStrategy(id, { pinned });
      const updated = resp.data;
      setItems(prev => {
        const sorted = reorderAfterPinChange(prev, { ...updated, id });
        if (!query.trim()) {
          persistOrder(sorted);
        }
        return sorted;
      });
      if (pinned) {
        setCurrentPage(1);
      }
    } catch (e) {
      setError('Failed to update stickiness');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteStrategy(id);
      setItems(prev => {
        const next = prev.filter(x => x.id !== id);
        if (!query.trim()) {
          persistOrder(next);
        }
        return next;
      });
      removeFromStoredOrder(id);
    } catch (e) {
      setError('Failed to delete strategy');
    }
  };

  // Layout calculations mirroring NotesDetails
  const totalCountLocal = items.length;
  const notesPerRow = numColumns;
  const totalRows = Math.ceil(totalCountLocal / notesPerRow);
  const pages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPageSafe = Math.min(currentPage, pages);
  const startRow = (currentPageSafe - 1) * pageSize;
  const endRow = Math.min(startRow + pageSize, totalRows);
  const startIndex = startRow * notesPerRow;
  const endIndex = Math.min(startIndex + (pageSize * notesPerRow), totalCountLocal);
  const visible = items.slice(startIndex, endIndex);

  const handlePageChange = (page) => setCurrentPage(page);
  const handlePageSizeChange = (e) => { const v = parseInt(e.target.value); setPageSize(v); localStorage.setItem('strategyPageSize', String(v)); setCurrentPage(1); };
  const handleColumnsChange = (e) => { const v = parseInt(e.target.value); setNumColumns(v); localStorage.setItem('strategyColumns', String(v)); };

  const renderPagination = () => {
    const itemsPag = [];
    const maxItems = 7;
    itemsPag.push(<Pagination.Item key={1} active={currentPageSafe === 1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
    let start = Math.max(2, currentPageSafe - 2);
    let end = Math.min(pages - 1, currentPageSafe + 2);
    if (currentPageSafe <= 3) end = Math.min(pages - 1, maxItems - 2);
    else if (currentPageSafe >= pages - 2) start = Math.max(2, pages - maxItems + 2);
    if (start > 2) itemsPag.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    for (let i = start; i <= end; i++) itemsPag.push(<Pagination.Item key={i} active={currentPageSafe === i} onClick={() => handlePageChange(i)}>{i}</Pagination.Item>);
    if (end < pages - 1) itemsPag.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    if (pages > 1) itemsPag.push(<Pagination.Item key={pages} active={currentPageSafe === pages} onClick={() => handlePageChange(pages)}>{pages}</Pagination.Item>);
    return (
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <div className="d-flex align-items-center">
          <span className="me-2">Rows per page:</span>
          <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} style={{ width: '80px' }}>
            <option value="3">3</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </Form.Select>
          <span className="ms-3 me-2">Columns:</span>
          <Form.Select size="sm" value={numColumns} onChange={handleColumnsChange} style={{ width: '80px' }}>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </Form.Select>
          <span className="ms-3">
            {totalCountLocal > 0 ? (
              <>Showing rows {startRow + 1} to {endRow} of {totalRows} ({startIndex + 1} to {endIndex} of {totalCountLocal} items)</>
            ) : 'No strategies found'}
          </span>
        </div>
        <Pagination size="sm">
          <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPageSafe === 1} />
          <Pagination.Prev onClick={() => handlePageChange(Math.max(1, currentPageSafe - 1))} disabled={currentPageSafe === 1} />
          {itemsPag}
          <Pagination.Next onClick={() => handlePageChange(Math.min(pages, currentPageSafe + 1))} disabled={currentPageSafe === pages} />
          <Pagination.Last onClick={() => handlePageChange(pages)} disabled={currentPageSafe === pages} />
        </Pagination>
      </div>
    );
  };

  const resetFilters = () => { setQuery(''); setCurrentPage(1); };

  // Function to highlight search terms in text
  const highlightSearchTerms = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <mark key={index} className="search-highlight">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleDragStart = (event, id) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(id));
    setDraggingId(id);
  };

  const handleDragEnter = (event, id) => {
    event.preventDefault();
    if (id === draggingId) return;
    setDragOverId(id);
  };

  const handleDragLeave = (event, id) => {
    event.preventDefault();
    if (dragOverId === id) {
      setDragOverId(null);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  const handleDrop = (event, targetId) => {
    event.preventDefault();
    const sourceIdRaw = draggingId ?? event.dataTransfer.getData('text/plain');
    const sourceId = sourceIdRaw ? String(sourceIdRaw) : null;
    const normalizedTargetId = String(targetId);
    if (!sourceId || sourceId === normalizedTargetId) {
      setDragOverId(null);
      return;
    }
    setItems(prev => {
      const next = [...prev];
      const fromIndex = next.findIndex(x => String(x.id) === sourceId);
      const toIndex = next.findIndex(x => String(x.id) === normalizedTargetId);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      if (!query.trim()) {
        persistOrder(next);
      }
      return next;
    });
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <Container className="py-4">
      <style>{customStyles}</style>
      <div className="mb-4">
        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-end">
            <Col xs={12} md={4}>
              <Form.Group className="mb-0 position-relative">
                <Form.Control
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setCurrentPage(1); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); } }}
                  placeholder={loading ? "Searching..." : "Search strategies..."}
                  size="sm"
                  className="border-0 shadow-sm"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    opacity: loading ? 0.7 : 1,
                    transition: 'opacity 0.2s ease'
                  }}
                />
                {loading && (
                  <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <div className="spinner-border spinner-border-sm text-muted" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs="auto" className="d-flex align-items-end">
              <Button
                variant="outline-secondary"
                size="sm"
                className="text-nowrap px-3"
                onClick={resetFilters}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Clear
              </Button>
            </Col>
            <Col className="d-flex align-items-end justify-content-end">
              <Button
                variant="primary"
                size="sm"
                className="text-nowrap"
                onClick={handleOpenModal}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Strategy
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div>
        {loading && showSkeleton ? (
          <Row className="g-3">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Col key={idx} md={Math.max(1, Math.min(12, Math.floor(12 / numColumns)))}>
                <div className="border rounded p-2">
                  <Placeholder as="div" animation="glow"><Placeholder xs={8} /></Placeholder>
                  <Placeholder as="div" animation="glow"><Placeholder xs={6} /></Placeholder>
                </div>
              </Col>
            ))}
          </Row>
        ) : loading ? null : (
          visible.length === 0 ? (
            <div className="text-muted">No strategies yet.</div>
          ) : (
            <Row className="g-3">
              {visible.map(s => {
                const isEditing = editingId === s.id;
                const dragHandlers = isEditing
                  ? {}
                  : {
                      draggable: true,
                      onDragStart: (event) => handleDragStart(event, s.id),
                      onDragEnter: (event) => handleDragEnter(event, s.id),
                      onDragOver: handleDragOver,
                      onDragLeave: (event) => handleDragLeave(event, s.id),
                      onDrop: (event) => handleDrop(event, s.id),
                      onDragEnd: handleDragEnd,
                    };

                return (
                  <Col key={s.id} md={Math.max(1, Math.min(12, Math.floor(12 / numColumns)))}>
                    <div
                      className={`strategy-card border rounded p-3 h-100 d-flex flex-column${!isEditing ? ' strategy-card-draggable' : ''}${draggingId === s.id ? ' strategy-card-dragging' : ''}${dragOverId === s.id ? ' strategy-card-drag-over' : ''}${s.pinned ? ' strategy-card-pinned' : ''}`}
                      style={{ whiteSpace: 'pre-wrap' }}
                      role="listitem"
                      aria-grabbed={!isEditing && draggingId === s.id}
                      {...dragHandlers}
                    >
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fw-bold fs-5 text-truncate d-flex align-items-center" title={s.name}>
                        <span className="me-1 flex-grow-1 text-truncate">{highlightSearchTerms(s.name, query)}</span>
                        {s.pinned && (
                          <i
                            className="bi bi-pin-angle-fill text-warning ms-1"
                            aria-label="Pinned strategy"
                            title="Pinned strategy"
                          ></i>
                        )}
                      </div>
                      <div className="d-flex gap-1 ms-2">
                        <Button
                          variant={s.pinned ? 'warning' : 'outline-warning'}
                          size="sm"
                          className="py-0 px-1"
                          title={s.pinned ? 'Unstick from top' : 'Stick to top'}
                          aria-label={s.pinned ? 'Unstick from top' : 'Stick to top'}
                          aria-pressed={s.pinned}
                          type="button"
                          onClick={() => handleTogglePinned(s.id, !s.pinned)}
                        >
                          <i className={`bi ${s.pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'}`}></i>
                        </Button>
                        {isEditing ? null : (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-0 px-1"
                            title="Edit"
                            aria-label="Edit"
                            onClick={() => {
                              setEditingId(s.id);
                              setEditingName(s.name);
                              setEditingEntryConditions(s.entry_conditions);
                              setEditingTradingPlan(s.trading_plan);
                            }}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                        )}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 px-1"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => handleDelete(s.id)}
                        >
                          <i className="bi bi-trash" style={{ color: '#dc3545' }}></i>
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 flex-grow-1">
                      {isEditing ? (
                        <>
                          <Form.Control
                            type="text"
                            className="mb-2"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            placeholder="Name"
                          />
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Control
                                as="textarea"
                                rows={5}
                                value={editingEntryConditions}
                                onChange={(e) => setEditingEntryConditions(e.target.value)}
                                placeholder="Entry Conditions"
                              />
                            </Col>
                            <Col md={8}>
                              <Form.Control
                                as="textarea"
                                rows={5}
                                value={editingTradingPlan}
                                onChange={(e) => setEditingTradingPlan(e.target.value)}
                                placeholder="Trading Plan"
                              />
                            </Col>
                          </Row>
                          <div className="mt-2 d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEdit(s.id)}
                              disabled={!editingName.trim() || !editingEntryConditions.trim() || !editingTradingPlan.trim()}
                            >
                              Save
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setEditingId(null);
                                setEditingName('');
                                setEditingEntryConditions('');
                                setEditingTradingPlan('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-muted small">{formatDateTime(s.updated_at || s.created_at)}</div>
                          <Row className="mt-2 g-3">
                            <Col md={4}>
                              <div className="fw-semibold mb-1" style={{ fontSize: '1.05rem' }}>Entry Conditions:</div>
                              <div>{highlightSearchTerms(s.entry_conditions, query)}</div>
                            </Col>
                            <Col md={8}>
                              <div className="fw-semibold mb-1" style={{ fontSize: '1.05rem' }}>Trading Plan:</div>
                              <div>{highlightSearchTerms(s.trading_plan, query)}</div>
                            </Col>
                          </Row>
                        </>
                      )}
                    </div>
                  </div>
                </Col>
              );
              })}
            </Row>
          )
        )}
      </div>

      {renderPagination()}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        backdrop={saving ? 'static' : true}
        keyboard={!saving}
        dialogClassName="modal-600w"
      >
        <Form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
          <Modal.Header closeButton={!saving}>
            <Modal.Title>New Strategy</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="strategyName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name"
                autoFocus
              />
            </Form.Group>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group controlId="strategyEntryConditions">
                  <Form.Label>Entry Conditions</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={newEntryConditions}
                    onChange={(e) => setNewEntryConditions(e.target.value)}
                    placeholder="Entry Conditions"
                  />
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group controlId="strategyTradingPlan">
                  <Form.Label>Trading Plan</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    value={newTradingPlan}
                    onChange={(e) => setNewTradingPlan(e.target.value)}
                    placeholder="Trading Plan"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={saving || !newName.trim() || !newEntryConditions.trim() || !newTradingPlan.trim()}
            >
              {saving ? 'Saving...' : 'Save Strategy'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default Strategies;
